// src/pages/dropshipping/JoinProviderPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader, AlertCircle, Store } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLocalization } from '../../contexts/LocalizationContext';
import { getProviderByInviteHash } from '../../services/api/dropshipping';
import styles from './JoinProviderPage.module.css';

const JoinProviderPage = () => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t } = useLocalization();

  const [loading, setLoading] = useState(true);
  const [providerInfo, setProviderInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!hash) {
      setErrorMsg(t('dropshipping.invalidInviteLink'));
      setLoading(false);
      return;
    }
    loadProviderInfo();
  }, [hash]);

  const loadProviderInfo = async () => {
    try {
      const data = await getProviderByInviteHash(hash);
      setProviderInfo(data);

      if (isAuthenticated && user?.business?.setupCompleted) {
        // Logged in and setup complete — go to products page, merchants tab, auto-open apply modal
        navigate('/dropshipping/products', {
          replace: true,
          state: {
            tab: 'merchants',
            openApplyForProviderId: data.providerBusinessId,
          },
        });
        return;
      }

      if (!isAuthenticated) {
        // Not logged in — save hash, redirect to signup
        localStorage.setItem('dropshippingInviteHash', hash);
        navigate('/signup', { replace: true });
        return;
      }

      // Logged in but setup not completed — just redirect to dashboard (setup wizard will handle it)
      // The hash is already stored in setupProgress from backend if signup happened with it.
      // Edge case: logged in + setup incomplete + opened invite link — store hash and go to dashboard.
      localStorage.setItem('dropshippingInviteHash', hash);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setErrorMsg(t('dropshipping.invalidInviteLink'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <Loader className={styles.spinner} size={40} />
          <p>{t('dropshipping.loadingProviderInfo')}</p>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <AlertCircle className={styles.errorIcon} size={40} />
          <h2>{t('dropshipping.invalidInviteLink')}</h2>
          <p>{errorMsg}</p>
        </div>
      </div>
    );
  }

  // Fallback (shouldn't normally render — redirects happen above)
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <Store size={40} />
        <p>{t('dropshipping.redirectingToSignup')}</p>
      </div>
    </div>
  );
};

export default JoinProviderPage;
